import React, { useState, useMemo } from 'react';
import Header from '../shared/header/Header';
import { generateAgentPackage } from './agentPackageUtils';
import { useGetAgentsListQuery, useDeployAgentMutation, useDeleteAgentMutation } from '../data/api/endPointsSlice';
import { AgentMarketPlaceItemWithFilter, AgentCardsFilterType, SortAgentsField, SortAgentsFieldsOrder } from '../pages/userAgent/agentMarketPlace/AgentMarketPlace';
import { useAppDispatch, useAppSelector } from '../reduxStore/redux-hooks';
import { resetAgentsFilters, setAgentsFiletrs } from '../reduxStore/slices/AgentsFiltersSlice';
import useAgentFiltering from '../shared/customHooks/useAgentFiltering/UseAgentFiltering';
import { agentFinderChatBotId } from '../shared/utiles/Utiles';
import AgentCardsList from './components/AgentCardsList';
import AgentEditModal from './components/AgentEditModal';
import AgentAddModal from './components/AgentAddModal';
import AgentFilterBar from './components/AgentFilterBar';
import AgentPagination from './components/AgentPagination';
import {
  Box,
  Typography,
  Button,
  CircularProgress,
  useTheme,
  useMediaQuery,
} from '@mui/material';

const Admin = () => {
  const [addModalOpen, setAddModalOpen] = useState(false);
  const [newAgentData, setNewAgentData] = useState<Partial<AgentMarketPlaceItemWithFilter & { folder_name?: string }>>({});
  const [isFileUploadMode, setIsFileUploadMode] = useState(false);
  const [uploadedFile, setUploadedFile] = useState<File | null>(null);
  const [fileUploadError, setFileUploadError] = useState('');
  const [deploymentSuccess, setDeploymentSuccess] = useState(false);
  const [fileUploadSuccess, setFileUploadSuccess] = useState(false);

  const handleAddChange = (field: keyof AgentMarketPlaceItemWithFilter, value: any) => {
    setNewAgentData((prev) => ({
      ...prev,
      [field]: value,
    }));
  };

  const handleAddSave = async () => {
    try {
      if (isFileUploadMode && uploadedFile && newAgentData._id) {
        // Deploy the uploaded ZIP file
        const result = await deployAgent({
          partyId: newAgentData._id,
          agentFile: uploadedFile
        }).unwrap();
        
        console.log('Agent deployed successfully:', result);
        setDeploymentSuccess(true);
        setFileUploadSuccess(false);
        alert('Agent added successfully!');
        // Close modal after successful deployment
        setTimeout(() => {
          setAddModalOpen(false);
          setNewAgentData({});
          setUploadedFile(null);
          setFileUploadError('');
          setDeploymentSuccess(false);
          setFileUploadSuccess(false);
        }, 500);
      } else if (!isFileUploadMode) {
        // For manual entry, generate ZIP on the fly and deploy
        if (!newAgentData._id || !newAgentData.name) {
          alert('Please provide required data (Party ID and Name) before deployment');
          return;
        }
        
        // Ensure tech_stack is always an array before generating package
        let tech_stack = newAgentData.tech_stack;
        if (typeof tech_stack === 'string') {
          tech_stack = (tech_stack as string).split(',').map((t: string) => t.trim()).filter(Boolean);
        }
        
        // Generate the ZIP file blob on the fly
        const zipBlob = await generateAgentPackage({ ...newAgentData, tech_stack });
        if (!zipBlob) {
          alert('Failed to generate agent package');
          return;
        }
        
        // Create a File object from the blob
        const folderName = (newAgentData.folder_name || newAgentData.name || '').replace(/[^a-zA-Z0-9-_]/g, '_').toLowerCase();
        const zipFile = new File([zipBlob], `${folderName}.zip`, { type: 'application/zip' });
        
        // Deploy the generated ZIP file
        const result = await deployAgent({
          partyId: newAgentData._id,
          agentFile: zipFile
        }).unwrap();
        
        console.log('Agent deployed successfully:', result);
        setDeploymentSuccess(true);
        alert('Agent added successfully!');
        // Close modal after successful deployment
        setTimeout(() => {
          setAddModalOpen(false);
          setNewAgentData({});
          setFileUploadError('');
          setDeploymentSuccess(false);
        }, 500);
      } else {
        alert('Missing required data for deployment');
        return;
      }
    } catch (error: any) {
      console.error('Error saving agent:', error);
      const errorMessage = error.data?.message || 'Failed to save agent. Please try again.';
      setFileUploadError(errorMessage);
      setDeploymentSuccess(false);
      alert(`Error: ${errorMessage}`);
    }
  };

  const handleFileUpload = async (event: React.ChangeEvent<HTMLInputElement>) => {
    const file = event.target.files?.[0];
    if (file) {
      setFileUploadError('');
      setDeploymentSuccess(false);
      setFileUploadSuccess(false);
      const allowedTypes = ['application/zip', 'application/x-zip-compressed'];
      const allowedExtensions = ['.zip'];

      const hasValidExtension = allowedExtensions.some(ext => file.name.toLowerCase().endsWith(ext));
      const hasValidType = allowedTypes.includes(file.type);

      if (!hasValidExtension && !hasValidType) {
        setFileUploadError('Please upload a valid ZIP folder');
        return;
      }

      setUploadedFile(file);

      // Handle ZIP files
      if (file.name.toLowerCase().endsWith('.zip')) {
        try {
          // Generate a unique party ID
          const partyId = crypto.randomUUID();

          // Populate the form data
          const baseName = file.name.replace('.zip', '').toLowerCase();
          setNewAgentData({
            _id: partyId,
            name: baseName,
            description: `Agent package uploaded from ${file.name.toLowerCase()}`,
            custom_ui: 'zip_package'
          });

          setFileUploadSuccess(true);
          setFileUploadError('');
        } catch (error: any) {
          console.error('Error processing file:', error);
          setFileUploadError(error.data?.message || 'Failed to process file. Please try again.');
          setUploadedFile(null);
        }
      } else {
        setFileUploadError('Only ZIP folders are supported for file upload');
      }
    }
  };

  const handleToggleUploadMode = (checked: boolean) => {
    setIsFileUploadMode(checked);
    setUploadedFile(null);
    setFileUploadError('');
    setDeploymentSuccess(false);
    setFileUploadSuccess(false);
    setNewAgentData({});
  };

  const handleAddAgent = () => {
    setNewAgentData({});
    setAddModalOpen(true);
    setIsFileUploadMode(false);
    setUploadedFile(null);
    setFileUploadError('');
    setDeploymentSuccess(false);
    setFileUploadSuccess(false);
  };

  const handleAgentsPageChange = (newPage: number) => {
    setPage(newPage);
  };
  const { data: agentList = [], isLoading: agentListLoading } = useGetAgentsListQuery();
  const [deployAgent, { isLoading: isDeployingAgent }] = useDeployAgentMutation();
  const [deleteAgent] = useDeleteAgentMutation();
  const [editData, setEditData] = useState<Partial<AgentMarketPlaceItemWithFilter & { folder_name?: string }>>({});
  const [modalOpen, setModalOpen] = useState(false);
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('sm'));

  // Add filter and search state
  const [searchTerm, setSearchTerm] = useState('');
  const [sortAgentsField, setSortAgentsField] = useState<SortAgentsField | ''>('');
  const [sortAgentFieldsOrder, setSortAgentFieldsOrder] = useState<SortAgentsFieldsOrder>(
    SortAgentsFieldsOrder.Ascending
  );
  const selectedFilters = useAppSelector((state) => state.agentsFilters.agentsFilters);
  const dispatch = useAppDispatch();

  // Add pagination state
  const [page, setPage] = useState(1);
  const agentsCardsPerPage = 15;

  // Filter out the agent finder chatbot from the agent list
  const filteredAgentList = useMemo(() => {
    return agentList.filter((agent) => agent._id !== agentFinderChatBotId);
  }, [agentList]);

  // Get all selected filter values for chip display
  const getAllSelectedFilters = () => {
    const allSelected = [];
    if (selectedFilters.category.length > 0) {
      allSelected.push(...selectedFilters.category.map((filter) => ({ value: filter, type: 'Domain / Industry' })));
    }
    return allSelected;
  };


  const removeFilter = (filterValue: string, filterType: string) => {
    if (filterType === 'Domain / Industry') {
      const newCategoryFilters = selectedFilters.category.filter((f: string) => f !== filterValue);
      dispatch(setAgentsFiletrs({ ...selectedFilters, category: newCategoryFilters }));
      setPage(1);
    }
    // Add similar logic for other filter types if needed
  };

  const hasSelectedFilters =
    selectedFilters.category.length > 0;

  const clearAgentFilters = () => {
    dispatch(resetAgentsFilters());
    setPage(1);
  };

  const {
    categoryList,
    filteredAgents,
    totalPageCount: totalPageCountOfAgents,
    paginatedAgents,
  } = useAgentFiltering(
    filteredAgentList,
    searchTerm,
    selectedFilters.category,
    selectedFilters.role,
    selectedFilters.cloudProvider,
    page,
    agentsCardsPerPage,
    sortAgentsField,
    sortAgentFieldsOrder
  );


  const handleChange = (field: keyof AgentMarketPlaceItemWithFilter | "readme_md", value: any) => {
    setEditData((prev) => ({
      ...prev,
      [field]: value,
    }));
  };

  // Add missing handleEdit stub for AgentCardsList
  const handleEdit = (agent: AgentMarketPlaceItemWithFilter) => {
    // Ensure readme_md is present for modal
    setEditData({
      ...agent,
    });
    setModalOpen(true);
  };

  const handleSave = () => {
    alert('Agent updated successfully');
    setEditData({});
    setModalOpen(false);
  };

  const handleCancel = () => {
    setEditData({});
    setModalOpen(false);
  }

  const handleToggleAgentsSortOrder = () => {
    setSortAgentFieldsOrder(
      sortAgentFieldsOrder === SortAgentsFieldsOrder.Ascending
        ? SortAgentsFieldsOrder.Descending
        : SortAgentsFieldsOrder.Ascending
    );
    setPage(1);
  };

  // Build agentCardsFilterSections for all filter types, matching AgentMarketPlace structure
  const agentCardsFilterSections = [
    {
      title: 'Domain / Industry',
      id: AgentCardsFilterType.Category,
      items: categoryList || [],
      selectedFilters: selectedFilters.category || [],
      setSelectedFilters: (filters: string[]) => dispatch(setAgentsFiletrs({ ...selectedFilters, category: filters })),
    },
  ];

  return (
    <Box>
      <Header />
      <Box sx={{ maxWidth: 1400, mx: 'auto', pt: '90px', pb: 2 }}>
        <Box
          sx={{
            backgroundColor: '#eaf6ff',
            padding: 2,
            borderRadius: 1,
            width: '97%',
            borderLeft: '4px solid #0d47a1',
            margin: '0 auto',
            mb: 2,
            display: 'flex',
            justifyContent: 'space-between',
            alignItems: 'center'
          }}
        >
          <Typography variant="subtitle1" sx={{ fontSize: '1.2rem' }} fontWeight="bold" color="#0d47a1">
            Admin Panel
          </Typography>
          <Button
            variant="outlined"
            sx={{
              borderRadius: 2,
              fontWeight: 600,
              fontSize: isMobile ? '0.9rem' : '1rem',
              px: isMobile ? 2 : 3,
              py: isMobile ? 1 : 1.2,
              boxShadow: 'none',
              border: '1px solid #e0e3e7',
              background: '#fff',
              color: '#111',
              textTransform: 'none',
              minWidth: 120,
              '&:hover': {
                backgroundColor: '#f8f9fa',
                borderColor: '#d0d7de',
              }
            }}
            onClick={handleAddAgent}
          >
            Add Agent
          </Button>
        </Box>

        {/* Modular Filter/Search/Sort Bar */}
        {agentListLoading ? (
          <Box sx={{ textAlign: 'center', mt: 4, mb: 4 }}>
            <CircularProgress size={32} sx={{ mb: 2 }} />
            <Typography variant="h6">Loading agents...</Typography>
          </Box>
        ) : (
          <AgentFilterBar
            agentCardsFilterSections={agentCardsFilterSections}
            filteredAgentList={filteredAgentList}
            agentListLoading={agentListLoading}
            filteredAgentsCount={filteredAgents.length}
            searchTerm={searchTerm}
            setSearchTerm={setSearchTerm}
            sortAgentsField={sortAgentsField}
            setSortAgentsField={setSortAgentsField}
            sortAgentFieldsOrder={sortAgentFieldsOrder}
            handleToggleAgentsSortOrder={handleToggleAgentsSortOrder}
            isMobile={isMobile}
            getAllSelectedFilters={getAllSelectedFilters}
            removeFilter={removeFilter}
            hasSelectedFilters={hasSelectedFilters}
            clearAgentFilters={clearAgentFilters}
            setPage={setPage}
          />
        )}
        {/* Edit Modal */}
        <AgentEditModal
          open={modalOpen}
          editData={editData}
          onChange={handleChange}
          onSave={handleSave}
          onCancel={handleCancel}
          onDownload={generateAgentPackage}
        />

        {/* Add Modal */}
        <AgentAddModal
          open={addModalOpen}
          newAgentData={newAgentData}
          isFileUploadMode={isFileUploadMode}
          uploadedFile={uploadedFile}
          fileUploadError={fileUploadError}
          deploymentSuccess={deploymentSuccess}
          isDeployingAgent={isDeployingAgent}
          fileUploadSuccess={fileUploadSuccess}
          onChange={handleAddChange}
          onSave={handleAddSave}
          onCancel={() => setAddModalOpen(false)}
          onFileUpload={handleFileUpload}
          onToggleUploadMode={handleToggleUploadMode}
        />

        <AgentCardsList
          agents={paginatedAgents}
          onEdit={handleEdit}
          onDelete={async (agentId, agentName) => {
            if (window.confirm(`Are you sure you want to delete agent: ${agentName || agentId}?`)) {
              try {
                await deleteAgent({ partyId: agentId }).unwrap();
                alert('Agent deleted successfully!');
              } catch (error) {
                alert('Failed to delete agent.');
              }
            }
          }}
        />
        <AgentPagination
          totalPageCount={totalPageCountOfAgents}
          page={page}
          onPageChange={handleAgentsPageChange}
          agentsCardsPerPage={agentsCardsPerPage}
          filteredAgentsLength={filteredAgents.length}
          isMobile={isMobile}
        />
        {filteredAgents.length === 0 && (
          <div style={{ marginTop: 32, textAlign: 'center' }}>
            <span>No agents found matching your criteria</span>
          </div>
        )}
      </Box>
    </Box>
  );
}
export default Admin;